import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React from 'react';
import { TreeListItem } from '../../lib/types/types';
import AiSummaryButton from './aiSummaryButton';

// Format date for display
const formatDate = (dateString: string): string => {
  try {
    // Treat input as UTC. If there's no timezone info, append 'Z' to force UTC parsing.
    let ds = dateString;
    if (typeof ds === "string") {
      const hasTimeZone = /(?:Z|[+\-]\d{2}:?\d{2}|[+\-]\d{4}|GMT|UTC|\([^)]+\))/i.test(ds);
      if (!hasTimeZone) {
        ds = ds + "Z";
      }
    }

    const date = new Date(ds);
    if (isNaN(date.getTime())) return "Unknown date";

    const locale =
      typeof navigator !== "undefined" && navigator.language
        ? navigator.language
        : "en-US";
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone, // format in the user's local time zone
    }).format(date);
  } catch (e) {
    return "Unknown date";
  }
};

interface TreeTableViewProps {
  trees: TreeListItem[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export const TreeTableView: React.FC<TreeTableViewProps> = ({
  trees,
  onEdit,
  onDelete,
}) => {
  return (
    <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                Name
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                Last Updated
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                Created
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="subtitle2" fontWeight="bold">
                Actions
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trees.map((tree) => (
            <TableRow
              key={tree.id}
              sx={{
                '&:hover': {
                  backgroundColor: 'grey.25',
                },
                '&:last-child td, &:last-child th': { border: 0 },
              }}
            >
              <TableCell>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onEdit(tree.id)}
                >
                  {tree.filename}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(tree.updatedAt || 'Unknown')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(tree.createdAt || 'Unknown')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <AiSummaryButton treeId={tree.id}/>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => onEdit(tree.id)}
                    variant="contained"
                  >
                    Edit
                  </Button>
                  {tree.canDelete ? (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(tree.id)}
                  >
                    <DeleteIcon />
                  </IconButton>) :(
                  <IconButton size="small" sx={{ visibility: 'hidden' }} aria-hidden="true">
                    <DeleteIcon />
                  </IconButton>)}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
